import type { FilterMenuConfig, FilterOption } from "@/shared/models/filterTypes";
import type { Portfolio } from "@/types/Portfolio";
import type { TagsWatchlist } from "@/shared/api/pref-storage/tags-watchlists";
import type { CategoryQuery } from "@/widget-components/newsquawk-headlines/models/CategoryQuery";
import {
  groupWatchlistsByCategory,
  NEWSQUAWK_CATEGORIES,
} from "@/pages/PortfolioBuilder/newsquawkCategories";
import { menuCategories } from "@/widget-components/newsquawk-headlines/models/categories";
import type { LibrarySection, LibraryGroup, LibraryCategory, LibraryItem } from "./LibraryBrowserContext";

// ── FilterMenuConfig → LibrarySection[] ─────────────────────────────────
// Used by the filter modal to convert existing widget filter configs
// into the unified library format.

// Label cleanup: "Filter by Ticker Watchlists" → type based on subsection
const SUB_LABELS: Record<string, { custom: string; newsquawk: string }> = {
  "ticker-watchlists": { custom: "Ticker Watchlists", newsquawk: "Newsquawk Tickers" },
  "keyword-watchlists": { custom: "Keyword Watchlists", newsquawk: "Newsquawk Keywords" },
  "tag-watchlists": { custom: "Tag Watchlists", newsquawk: "Newsquawk Tags" },
  "category": { custom: "Advanced Filters", newsquawk: "Newsquawk Advanced Filters" },
};

function subsectionLabel(configId: string, subId: string, fallbackLabel: string): string {
  // Strip -filter / -highlight suffix to get the base type
  const baseId = configId.replace(/-filter$/, "").replace(/-highlight$/, "");
  const labels = SUB_LABELS[baseId];
  if (!labels) return fallbackLabel;
  if (subId.startsWith("user-") || subId.startsWith("your-")) return labels.custom;
  if (subId.startsWith("newsquawk-")) return labels.newsquawk;
  return fallbackLabel;
}

export interface FilterCreateCallbacks {
  onCreateTicker?: () => void;
  onCreateKeyword?: () => void;
  onCreateTag?: () => void;
  onCreateAdvanced?: () => void;
}

export function filterConfigToLibrarySections(
  configs: FilterMenuConfig[],
  portfolios?: Portfolio[],
  createCallbacks?: FilterCreateCallbacks
): LibrarySection[] {
  // Build a lookup for enriching items with portfolio metadata
  const portfolioMap = new Map<string, Portfolio>();
  if (portfolios) {
    for (const p of portfolios) {
      portfolioMap.set(String(p.id), p);
    }
  }
  // Map subsection IDs to create callbacks
  const callbackForSub = (subId: string): (() => void) | undefined => {
    if (!createCallbacks) return undefined;
    if (subId.startsWith("user-tickers") || subId.startsWith("your-tickers")) return createCallbacks.onCreateTicker;
    if (subId.startsWith("user-keywords") || subId.startsWith("your-keywords")) return createCallbacks.onCreateKeyword;
    if (subId.startsWith("user-tags") || subId.startsWith("your-tags")) return createCallbacks.onCreateTag;
    if (subId.startsWith("user-filters") || subId.startsWith("your-filters")) return createCallbacks.onCreateAdvanced;
    return undefined;
  };

  const sections: LibrarySection[] = [];

  for (const config of configs) {
    if (config.subsections && config.subsections.length > 0) {
      // Flatten: each subsection becomes its own top-level section
      for (const sub of config.subsections) {
        const subItems = sub.filterIds
          .map((id) => config.filterOptions.find((opt) => opt.id === id))
          .filter((opt): opt is FilterOption => !!opt && !opt.isHeader);

        if (subItems.length === 0 && !sub.createNewLink) continue;

        const categories = extractCategoriesFromOptions(config.filterOptions, sub.filterIds);
        const label = subsectionLabel(config.id, sub.id, sub.label);

        sections.push({
          id: `${config.id}--${sub.id}`,
          label,
          groups: [{
            id: sub.id,
            label,
            items: subItems.map((opt) => enrichItem({
              id: String(opt.id),
              label: opt.label,
              disabled: opt.disabled ?? config.disabled,
            }, portfolioMap, config.id)),
            categories: categories.length > 0 ? categories : undefined,
            createAction: (() => {
              const cb = callbackForSub(sub.id);
              if (cb) return { label: sub.createNewLink?.label ?? "Create new...", onClick: cb };
              if (sub.createNewLink) return { label: sub.createNewLink.label ?? "Create new...", onClick: () => { window.location.href = sub.createNewLink!.url; } };
              return undefined;
            })(),
          }],
        });
      }
    } else {
      // No subsections — single flat section
      const items = config.filterOptions
        .filter((opt) => !opt.isHeader)
        .map((opt) => enrichItem({
          id: String(opt.id),
          label: opt.label,
          disabled: opt.disabled ?? config.disabled,
        }, portfolioMap, config.id));

      const categories = extractCategoriesFromOptions(config.filterOptions, config.filterOptions.map((o) => o.id));

      // Clean up label
      let label = config.label;
      if (label.startsWith("Filter by ")) label = label.replace("Filter by ", "");
      if (label.startsWith("Highlight by ")) label = label.replace("Highlight by ", "");

      sections.push({
        id: config.id,
        label,
        groups: [{
          id: config.id,
          label,
          items,
          categories: categories.length > 0 ? categories : undefined,
          createAction: (() => {
            // Check for callback based on config ID
            const cb = config.id.includes("category") ? createCallbacks?.onCreateAdvanced : undefined;
            if (cb) return { label: config.createNewLink?.label ?? "Create new...", onClick: cb };
            if (config.createNewLink) return { label: config.createNewLink.label ?? "Create new...", onClick: () => { window.location.href = config.createNewLink!.url; } };
            return undefined;
          })(),
        }],
      });
    }
  }

  // Reorder: custom first, then newsquawk (as reference), then other (Asset Class, Advanced Filters, etc.)
  const isCustom = (s: LibrarySection) => s.id.includes("user-") || s.id.startsWith("my-");
  const isNewsquawk = (s: LibrarySection) => s.id.includes("newsquawk-") || s.id.startsWith("nq-");

  const custom = sections.filter(isCustom);
  const newsquawk = sections
    .filter(isNewsquawk)
    .map((s) => ({ ...s, sectionVariant: "reference" as const }));
  const other = sections.filter((s) => !isCustom(s) && !isNewsquawk(s));

  return [...custom, ...newsquawk, ...other];
}

/** Enrich a library item with portfolio or tag metadata */
function enrichItem(item: LibraryItem, portfolioMap: Map<string, Portfolio>, configId?: string): LibraryItem {
  // Try portfolio match first
  const portfolio = portfolioMap.get(item.id);
  if (portfolio) {
    const isKeywordOnly = (portfolio.keywords?.length ?? 0) > 0 && portfolio.tickers.length === 0;
    return {
      ...item,
      color: portfolio.color,
      metadata: {
        ownerType: portfolio.owner_type,
        itemType: isKeywordOnly ? "keyword" : "ticker",
        tickers: portfolio.tickers,
        keywords: portfolio.keywords ?? [],
        priority: portfolio.priority,
        notifications: portfolio.notifications,
        portfolioId: portfolio.id,
      },
    };
  }

  // Tag items — detect from config ID
  if (configId && configId.includes("tag-watchlists")) {
    return {
      ...item,
      metadata: {
        itemType: "tag",
        tags: [{ id: item.id, name: item.label }],
      },
    };
  }

  // Asset class / other items — add label as metadata for display
  if (configId && (configId.includes("basic-filter") || configId.includes("category-filter"))) {
    return {
      ...item,
      metadata: {
        itemType: configId.includes("basic-filter") ? "asset-class" : "advanced-filter",
      },
    };
  }

  return item;
}

/** Extract category boundaries from isHeader items in the options list */
function extractCategoriesFromOptions(
  allOptions: FilterOption[],
  scopedIds: (string | number)[]
): LibraryCategory[] {
  const scopedOptions = scopedIds
    .map((id) => allOptions.find((opt) => opt.id === id))
    .filter((opt): opt is FilterOption => !!opt);

  const categories: LibraryCategory[] = [];
  let currentCategory: LibraryCategory | null = null;

  for (const opt of scopedOptions) {
    if (opt.isHeader) {
      currentCategory = { id: String(opt.id), label: opt.label, itemIds: [] };
      categories.push(currentCategory);
    } else if (currentCategory) {
      currentCategory.itemIds.push(String(opt.id));
    }
  }

  return categories;
}

// ── Portfolio/Tag/Query → LibrarySection[] ──────────────────────────────
// Used by the Watchlist Builder page.

export function getWatchlistType(p: Portfolio): "ticker-only" | "keyword-only" | "mixed" {
  const hasTickers = p.tickers.length > 0;
  const hasKeywords = (p.keywords?.length ?? 0) > 0;
  if (hasTickers && hasKeywords) return "mixed";
  if (hasKeywords) return "keyword-only";
  return "ticker-only";
}

export function portfolioToLibrarySections(
  portfolios: Portfolio[],
  tagWatchlists: TagsWatchlist[],
  advancedFilters: CategoryQuery[],
  options?: {
    tagIdToName?: Record<string, string>;
    onCreateTicker?: () => void;
    onCreateKeyword?: () => void;
    onCreateTag?: () => void;
    onCreateAdvanced?: () => void;
  }
): LibrarySection[] {
  const tickerPortfolios = portfolios.filter((p) => getWatchlistType(p) !== "keyword-only");
  const keywordPortfolios = portfolios.filter((p) => getWatchlistType(p) !== "ticker-only");

  const userTickers = tickerPortfolios.filter((p) => p.owner_type === "USER");
  const newsquawkTickers = tickerPortfolios.filter((p) => p.owner_type === "NEWSQUAWK");
  const userKeywords = keywordPortfolios.filter((p) => p.owner_type === "USER");
  const newsquawkKeywords = keywordPortfolios.filter((p) => p.owner_type === "NEWSQUAWK");

  const userTags = tagWatchlists.filter((tw) => tw.owner_type === "USER");
  const newsquawkTags = tagWatchlists.filter((tw) => tw.owner_type !== "USER");
  const userFilters = advancedFilters.filter((f) => f.owner_type === "USER");
  const newsquawkFilters = advancedFilters.filter((f) => f.owner_type === "NEWSQUAWK");

  const sections: LibrarySection[] = [];

  // ════════════════════════════════════════════════════════════
  // Custom sections first
  // ════════════════════════════════════════════════════════════

  if (userTickers.length > 0 || options?.onCreateTicker) {
    sections.push({
      id: "my-ticker-watchlists",
      label: "Ticker Watchlists",
      groups: [{
        id: "user-tickers",
        label: "Ticker Watchlists",
        items: userTickers.map((p) => portfolioToItem(p, "ticker")),
        createAction: options?.onCreateTicker
          ? { label: "Create new...", onClick: options.onCreateTicker }
          : undefined,
      }],
    });
  }

  if (userKeywords.length > 0 || options?.onCreateKeyword) {
    sections.push({
      id: "my-keyword-watchlists",
      label: "Keyword Watchlists",
      groups: [{
        id: "user-keywords",
        label: "Keyword Watchlists",
        items: userKeywords.map((p) => portfolioToItem(p, "keyword")),
        createAction: options?.onCreateKeyword
          ? { label: "Create new...", onClick: options.onCreateKeyword }
          : undefined,
      }],
    });
  }

  if (userTags.length > 0 || options?.onCreateTag) {
    sections.push({
      id: "my-tag-watchlists",
      label: "Tag Watchlists",
      groups: [{
        id: "user-tags",
        label: "Tag Watchlists",
        items: userTags.map((tw) => tagWatchlistToItem(tw, options?.tagIdToName)),
        createAction: options?.onCreateTag
          ? { label: "Create new...", onClick: options.onCreateTag }
          : undefined,
      }],
    });
  }

  if (userFilters.length > 0 || options?.onCreateAdvanced) {
    sections.push({
      id: "my-filters",
      label: "Advanced Filters",
      groups: [{
        id: "user-filters",
        label: "Advanced Filters",
        items: userFilters.map((f) => advancedFilterToItem(f, options?.tagIdToName)),
        createAction: options?.onCreateAdvanced
          ? { label: "Create new...", onClick: options.onCreateAdvanced }
          : undefined,
      }],
    });
  }

  // ════════════════════════════════════════════════════════════
  // Newsquawk Library — single drawer with multiple groups
  // ════════════════════════════════════════════════════════════

  const nqGroups: LibraryGroup[] = [];

  if (newsquawkTickers.length > 0) {
    const grouped = groupWatchlistsByCategory(newsquawkTickers);
    const categories: LibraryCategory[] = [];
    const items: LibraryItem[] = [];

    for (const catConfig of NEWSQUAWK_CATEGORIES) {
      const catPortfolios = grouped[catConfig.id];
      if (catPortfolios.length === 0) continue;
      categories.push({
        id: catConfig.id,
        label: catConfig.label,
        itemIds: catPortfolios.map((p) => String(p.id)),
      });
      for (const p of catPortfolios) {
        items.push(portfolioToItem(p, "ticker"));
      }
    }

    nqGroups.push({
      id: "newsquawk-tickers",
      label: "Tickers",
      items,
      categories,
      createAction: options?.onCreateTicker
        ? { label: "Create your own custom ticker watchlist", onClick: options.onCreateTicker }
        : undefined,
    });
  }

  if (newsquawkKeywords.length > 0) {
    const grouped = groupWatchlistsByCategory(newsquawkKeywords);
    const categories: LibraryCategory[] = [];
    const items: LibraryItem[] = [];

    for (const catConfig of NEWSQUAWK_CATEGORIES) {
      const catPortfolios = grouped[catConfig.id];
      if (catPortfolios.length === 0) continue;
      categories.push({
        id: catConfig.id,
        label: catConfig.label,
        itemIds: catPortfolios.map((p) => String(p.id)),
      });
      for (const p of catPortfolios) {
        items.push(portfolioToItem(p, "keyword"));
      }
    }

    nqGroups.push({
      id: "newsquawk-keywords",
      label: "Keywords",
      items,
      categories,
      createAction: options?.onCreateKeyword
        ? { label: "Create your own custom keyword watchlist", onClick: options.onCreateKeyword }
        : undefined,
    });
  }

  // Newsquawk Tags — individual tags from menuCategories, grouped by category
  {
    const categories: LibraryCategory[] = [];
    const items: LibraryItem[] = [];

    for (const tw of newsquawkTags) {
      items.push(tagWatchlistToItem(tw, options?.tagIdToName));
    }

    for (const category of menuCategories) {
      const catItemIds: string[] = [];
      for (const catItem of category.items) {
        const itemId = String(catItem.id);
        catItemIds.push(itemId);
        items.push({
          id: itemId,
          label: catItem.title,
          metadata: {
            itemType: "tag",
            tags: [{ id: itemId, name: catItem.title }],
          },
        });
      }
      categories.push({
        id: category.title.toLowerCase().replace(/\s+/g, "-"),
        label: category.title,
        itemIds: catItemIds,
      });
    }

    if (items.length > 0) {
      nqGroups.push({
        id: "newsquawk-tags",
        label: "Tags",
        items,
        categories: categories.length > 0 ? categories : undefined,
        createAction: options?.onCreateTag
          ? { label: "Create your own custom tag watchlist", onClick: options.onCreateTag }
          : undefined,
      });
    }
  }

  if (newsquawkFilters.length > 0) {
    nqGroups.push({
      id: "newsquawk-filters",
      label: "Advanced Filters",
      items: newsquawkFilters.map((f) => advancedFilterToItem(f, options?.tagIdToName)),
      createAction: options?.onCreateAdvanced
        ? { label: "Create your own custom advanced filter", onClick: options.onCreateAdvanced }
        : undefined,
    });
  }

  if (nqGroups.length > 0) {
    sections.push({
      id: "nq-library",
      label: "Newsquawk Library",
      groups: nqGroups,
      sectionVariant: "reference",
    });
  }

  return sections;
}

function portfolioToItem(p: Portfolio, itemType: "ticker" | "keyword"): LibraryItem {
  return {
    id: String(p.id),
    label: p.name,
    color: p.color,
    metadata: {
      ownerType: p.owner_type,
      itemType,
      tickers: p.tickers,
      keywords: p.keywords ?? [],
      priority: p.priority,
      notifications: p.notifications,
      portfolioId: p.id,
    },
  };
}

function tagWatchlistToItem(
  tw: TagsWatchlist,
  tagIdToName?: Record<string, string>
): LibraryItem {
  const tags = tw.tags.map((id) => ({
    id,
    name: tagIdToName?.[id] ?? `Tag #${id}`,
  }));

  return {
    id: `tag-${tw.id}`,
    label: tw.name,
    metadata: {
      ownerType: tw.owner_type,
      itemType: "tag",
      tags,
      notifications: tw.notifications,
      tagWatchlistId: tw.id,
    },
  };
}

function advancedFilterToItem(
  f: CategoryQuery,
  tagIdToName?: Record<string, string>
): LibraryItem {
  const filterPortfolios = (f.portfolios ?? []).map((p) => {
    const type = getWatchlistType(p);
    return {
      name: p.name,
      color: p.color,
      type: type === "keyword-only" ? "keywords" : "tickers",
      items: type === "keyword-only" ? (p.keywords ?? []) : p.tickers,
    };
  });

  const filterTags = (f.categories ?? []).map((id) => ({
    id,
    name: tagIdToName?.[id] ?? `Tag #${id}`,
  }));

  const filterTagsWatchlists = (f.tagsWatchlists ?? []).map((tw) => ({
    name: tw.name,
    tagCount: tw.tags.length,
    tags: tw.tags.map((id) => ({
      id,
      name: tagIdToName?.[id] ?? `Tag #${id}`,
    })),
  }));

  return {
    id: `filter-${f.id}`,
    label: f.queryName,
    metadata: {
      ownerType: f.owner_type,
      itemType: "advanced-filter",
      searchKeyword: f.searchKeyword,
      filterPortfolios,
      filterTags,
      filterTagsWatchlists,
      filterLogic: {
        filterMode: f.filterMode,
        keywordOp: f.keywordOp,
        keywordWatchlistOp: f.keywordWatchlistOp,
      },
      notifications: f.notifications,
      filterId: f.id,
    },
  };
}
